import {
  Component,
  type CSSProperties,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import i18next from 'i18next';
import { getPortalName } from '../hooks/usePortalName';

// Last line of defence: without this, an uncaught render error makes React
// unmount the whole tree and the user is left staring at a blank body
// background. The fallback deliberately depends on nothing that could be the
// thing that crashed — no MUI theme, router, redux store, or translation
// provider — so it uses inline styles and i18next with English defaults.

type Props = { children: ReactNode };
type State = {
  error: Error | null;
  occurredAt: string | null;
  copied: boolean;
};

const COPIED_RESET_MS = 2000;

const t = (key: string, defaultValue: string) => {
  try {
    return i18next.t(key, { ns: 'common', defaultValue });
  } catch {
    return defaultValue;
  }
};

const resolveLogo = () => {
  try {
    const portalName = getPortalName();
    return portalName
      ? `/${portalName}/brand-logo-primary.svg`
      : '/al-logo-dark.png';
  } catch {
    return '/al-logo-dark.png';
  }
};

// Matches the default theme's primary blue (src/theme/foundations/palette.ts).
// Hard-coded on purpose: the theme may be what crashed.
const PRIMARY = '#197CB3';

const styles: Record<string, CSSProperties> = {
  page: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    // Start-aligned with auto margins on the card: centred when it fits, but
    // when it is taller than the viewport its top stays reachable by scrolling.
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: 16,
    backgroundColor: '#F5F6F7',
    fontFamily: '"Source Sans Pro", Arial, sans-serif',
    color: '#011012',
    overflowY: 'auto',
  },
  card: {
    margin: 'auto 0',
    width: '100%',
    maxWidth: 520,
    boxSizing: 'border-box',
    padding: '40px 32px',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    boxShadow:
      '0 1px 3px rgba(1, 16, 18, 0.08), 0 8px 24px rgba(1, 16, 18, 0.06)',
    textAlign: 'left',
  },
  logo: {
    display: 'block',
    height: 48,
    maxWidth: '70%',
    objectFit: 'contain',
    objectPosition: 'left',
    marginBottom: 32,
  },
  title: { margin: '0 0 12px', fontSize: 26, fontWeight: 700 },
  body: {
    margin: '0 0 28px',
    fontSize: 17,
    lineHeight: 1.5,
    color: '#01101299',
  },
  actions: {
    display: 'flex',
    gap: 12,
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    marginBottom: 28,
  },
  primaryButton: {
    padding: '10px 24px',
    fontSize: 16,
    fontWeight: 600,
    fontFamily: 'inherit',
    color: '#FFFFFF',
    backgroundColor: PRIMARY,
    border: `1px solid ${PRIMARY}`,
    borderRadius: 8,
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '10px 24px',
    fontSize: 16,
    fontWeight: 600,
    fontFamily: 'inherit',
    color: PRIMARY,
    backgroundColor: 'transparent',
    border: `1px solid ${PRIMARY}`,
    borderRadius: 8,
    cursor: 'pointer',
  },
  details: {
    textAlign: 'left',
    fontSize: 13,
    color: '#01101299',
    borderTop: '1px solid #0110121A',
    paddingTop: 16,
  },
  summary: { cursor: 'pointer', fontWeight: 600, color: PRIMARY },
  pre: {
    margin: '12px 0 8px',
    padding: 12,
    maxHeight: 200,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: 12,
    backgroundColor: '#F5F6F7',
    borderRadius: 6,
  },
  linkButton: {
    padding: 0,
    fontSize: 13,
    fontFamily: 'inherit',
    color: PRIMARY,
    textDecoration: 'underline',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  },
  // Same box as linkButton so nothing shifts, but reads as plain text.
  copiedLabel: {
    padding: 0,
    fontSize: 13,
    fontFamily: 'inherit',
    color: 'inherit',
    background: 'none',
    border: 'none',
    cursor: 'default',
  },
};

export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null, occurredAt: null, copied: false };

  private copiedTimer: ReturnType<typeof setTimeout> | undefined;

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, occurredAt: new Date().toISOString() };
  }

  componentWillUnmount() {
    clearTimeout(this.copiedTimer);
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(
      '[AppErrorBoundary] Uncaught render error',
      error,
      info.componentStack,
    );
  }

  private getReport() {
    const { error, occurredAt } = this.state;
    return [
      `Time: ${occurredAt}`,
      // Origin + path only: query strings can carry secrets (e.g. the
      // VERIFICATION_TOKEN on account-creation and email-verification links).
      `URL: ${window.location.origin}${window.location.pathname}`,
      `Error: ${error?.message}`,
      error?.stack ? `\n${error.stack}` : '',
    ].join('\n');
  }

  private copyReport = () => {
    void navigator.clipboard
      ?.writeText(this.getReport())
      .then(() => {
        // Briefly swap the label to confirm the copy, then put it back.
        this.setState({ copied: true });
        clearTimeout(this.copiedTimer);
        this.copiedTimer = setTimeout(
          () => this.setState({ copied: false }),
          COPIED_RESET_MS,
        );
      })
      .catch(() => undefined);
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <div style={styles.page} role="alert">
        <div style={styles.card}>
          <img src={resolveLogo()} alt="" style={styles.logo} />
          <h1 style={styles.title}>
            {t('appError.title', 'This Page Stopped Working')}
          </h1>
          <p style={styles.body}>
            {t(
              'appError.message',
              'An unexpected error occurred. Reload the page to try again. If it keeps happening, contact support.',
            )}
          </p>
          <div style={styles.actions}>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => window.location.assign('/')}>
              {t('appError.home', 'Go to home')}
            </button>
            <button
              type="button"
              style={styles.primaryButton}
              onClick={() => window.location.reload()}>
              {t('appError.reload', 'Reload page')}
            </button>
          </div>
          <details style={styles.details}>
            <summary style={styles.summary}>
              {t('appError.details', 'Show error details')}
            </summary>
            <pre style={styles.pre}>{this.getReport()}</pre>
            <button
              type="button"
              style={this.state.copied ? styles.copiedLabel : styles.linkButton}
              onClick={this.copyReport}
              disabled={this.state.copied}
              aria-live="polite">
              {this.state.copied
                ? t('appError.copied', 'Copied')
                : t('appError.copy', 'Copy details')}
            </button>
          </details>
        </div>
      </div>
    );
  }
}
